import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import ConsoleOutput from './ConsoleOutput';

const ExecutionResults = ({ job, layer, functionName = 'COMPOSITE_0', hardware = 'NVIDIA A100' }) => {
  const isTestRunning = job?.status === 'running' || job?.status === 'pending' || job?.status === 'queued';

  const getTestResult = () => {
    // If test is currently running, don't show stale results
    if (isTestRunning) {
      return 'N/A';
    }
    
    // Prioritize current job result over layer's persisted status
    if (job?.result?.test_result) {
      return job.result.test_result;
    }
    if (layer?.last_execution_status) {
      return layer.last_execution_status === 'success' ? 'PASSED' : 'FAILED';
    }
    return 'N/A';
  };

  const getTestResultStyle = () => {
    const result = getTestResult();
    if (result === 'PASSED') return 'text-green-600 font-semibold';
    if (result === 'FAILED') return 'text-red-600 font-semibold';
    return 'text-gray-600';
  };

  const getExecutionTime = () => {
    const layerMetrics = layer?.last_execution_metrics;
    const jobMetrics = job?.result?.metrics;

    const metrics = layerMetrics || jobMetrics;
    if (metrics?.median_time_ms && metrics?.rounds) {
      return `${metrics.median_time_ms}ms, median of ${metrics.rounds} runs`;
    }
    if (metrics?.execution_time_ms) {
      return `${metrics.execution_time_ms}ms`;
    }
    return 'N/A';
  };

  const ioMetadata = layer?.io_metadata;
  const inputs = ioMetadata?.inputs || [];
  const outputs = ioMetadata?.outputs;
  const outputArray = outputs ? (Array.isArray(outputs) ? outputs : [outputs]) : [];

  const [activeTab, setActiveTab] = useState('results');

  return (
    <div className="execution-results h-full bg-white border border-gray-300 rounded-lg flex flex-col">
      {/* Tab Bar */}
      <div className="flex border-b border-gray-300">
        <button
          onClick={() => setActiveTab('results')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'results'
              ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          Execution Results
        </button>
        <button
          onClick={() => setActiveTab('console')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'console'
              ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          Console
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'results' ? (
        <div className="flex-1 p-4 overflow-auto">
          <div className="grid grid-cols-2 gap-4 h-full">
        {/* Left: IO Specification */}
        <div>
          <div className="flex items-center mb-3">
            <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
            <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">I/O Specification</span>
          </div>
          
          <div className="text-xs flex items-center mb-3">
            <span className="text-gray-500 w-16">Function</span>
            <span className="text-gray-900 font-medium">{functionName}</span>
          </div>

          {inputs.length > 0 ? (
            <div className="space-y-1">
              {inputs.map((input, i) => {
                const shape = input.shape || [];
                const dtype = input.dtype || 'float32';
                const shapeStr = shape.length > 0 ? `[${shape.join(', ')}]` : '[]';
                return (
                  <div key={i} className="text-xs flex items-center">
                    <span className="text-gray-500 w-16">Input {i}</span>
                    <span className="text-gray-500 w-12">shape:</span>
                    <span className="text-purple-600 font-mono w-28">{shapeStr}</span>
                    <span className="text-gray-500 w-10">type:</span>
                    <span className="text-gray-800 font-mono">{dtype}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-xs text-gray-400">No input specification</div>
          )}

          {outputArray.length > 0 && (
            <div className="space-y-1 mt-2">
              {outputArray.map((output, i) => {
                const shape = output.shape || [];
                const dtype = output.dtype || 'float32';
                const shapeStr = shape.length > 0 ? `[${shape.join(', ')}]` : '[]';
                return (
                  <div key={i} className="text-xs flex items-center">
                    <span className="text-green-600 w-16">Output {i + 1}</span>
                    <span className="text-green-600 w-12">shape:</span>
                    <span className="text-purple-600 font-mono w-28">{shapeStr}</span>
                    <span className="text-green-600 w-10">type:</span>
                    <span className="text-gray-800 font-mono">{dtype}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-l border-gray-300 pl-4">
          {/* Right: Test Results */}
          <div className="flex items-center mb-3">
            <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
            <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Test Results</span>
          </div>

          <div className="space-y-2">
            <div className="text-xs flex items-center">
              <span className="text-gray-500 w-24">Test Result</span>
              {isTestRunning ? (
                <span className="text-blue-600 flex items-center">
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  Running...
                </span>
              ) : (
                <span className={getTestResultStyle()}>{getTestResult()}</span>
              )}
            </div>

            <div className="text-xs flex items-center">
              <span className="text-gray-500 w-24">Execution Time</span>
              {isTestRunning ? (
                <span className="text-blue-600 flex items-center">
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  Running...
                </span>
              ) : (
                <span className="text-gray-800">{getExecutionTime()}</span>
              )}
            </div>

            <div className="text-xs flex items-center">
              <span className="text-gray-500 w-24">Baseline Time</span>
              <span className="text-gray-800">N/A</span>
            </div>
          </div>
        </div>
      </div>
        </div>
      ) : (
        <div className="flex-1 overflow-hidden">
          <ConsoleOutput job={job} />
        </div>
      )}
    </div>
  );
};

export default ExecutionResults;
